import { z } from 'zod';
import { emailSchema } from './common.js';
// -------- P1 self-serve signup (0052) --------
/** Portal slug: lowercase a-z0-9 + hyphens, 3–40 chars, no leading/trailing
 *  or doubled hyphen. Uppercase input is folded rather than rejected —
 *  people type company names. */
export const signupSlugSchema = z
    .string()
    .trim()
    .transform((s) => s.toLowerCase())
    .pipe(z
    .string()
    .min(3, 'At least 3 characters')
    .max(40, 'At most 40 characters')
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers and hyphens only'));
export const signupSlugCheckQuerySchema = z.object({
    slug: signupSlugSchema,
});
/**
 * 0084 — a comp code as typed. Canonicalised here (uppercased, spaces and
 * hyphens stripped) so the server only ever sees the stored form; the
 * alphabet excludes 0 1 I L O U, so a misread character fails the shape
 * rather than silently matching a different code.
 */
export const compCodeSchema = z
    .string()
    .trim()
    .transform((s) => s.toUpperCase().replace(/[\s-]+/g, ''))
    .pipe(z.string().regex(/^[2-9A-HJKMNP-TV-Z]{8,10}$/, "That code isn't valid"));
export const compCodeCheckQuerySchema = z.object({
    code: compCodeSchema,
});
/** Step 1 — request the verification code. Rides the existing OTP rail;
 *  this schema exists for the signup-specific wrapper (blocklist + events). */
/** Referrer hostname or utm_source. Host-only and capped in the service —
 *  a full URL with a query string could carry a token, so it never lands
 *  in a funnel row verbatim. */
const signupSourceSchema = z.string().trim().max(200);
export const signupStartBodySchema = z
    .object({
    name: z.string().trim().min(1, 'Your name is required').max(120),
    email: emailSchema,
    companyName: z.string().trim().min(1, 'Company name is required').max(160),
    source: signupSourceSchema.optional(),
})
    .strict();
/**
 * The wizard's three-way address choice. A closed enum, not a string: it
 * rides into a funnel row, and instrument.ts's redaction property depends
 * on every recorded value being from a fixed set.
 */
export const domainDoorSchema = z.enum(['free', 'buy', 'byo']);
/** Step 3 — provision on submit. signupToken comes from otp/verify's signup
 *  branch; name/companyName echo step 1 (the server trusts the verified
 *  email, not the cosmetics). turnstileToken is validated only when the
 *  TURNSTILE feature flag is on. */
export const signupCompleteBodySchema = z
    .object({
    signupToken: z.string().min(16).max(128),
    name: z.string().trim().min(1).max(120),
    companyName: z.string().trim().min(1).max(160),
    slug: signupSlugSchema,
    turnstileToken: z.string().max(2048).optional(),
    /** 0084: optional comp code. Redemption is decided server-side inside
     *  the provisioning transaction — the client's earlier check is a
     *  courtesy, never an authority. */
    compCode: compCodeSchema.optional(),
    /**
     * Step-3 default telemetry (address-default unit). Which door was
     * pre-selected on entry, which one was actually submitted, and whether
     * the pre-filled slug was hand-edited — so the digest can separate
     * "default accepted" from "default changed".
     *
     * All optional: an older cached bundle that doesn't send them must
     * still be able to provision. Cosmetic in every sense — none of the
     * three changes what gets built, only what gets counted.
     */
    defaultDoor: domainDoorSchema.optional(),
    chosenDoor: domainDoorSchema.optional(),
    slugEdited: z.boolean().optional(),
})
    .strict();
/**
 * The wizard's per-step view beacon (funnel-emails unit). Public and
 * unauthenticated — it fires before a fleet or a session exists — so it
 * carries nothing but which step was reached and, optionally, who and from
 * where. Server-side and cheap, deliberately separate from the client
 * web-beacon's wizard_step: that one is session-scoped and bot-bucketed for
 * the traffic console, this one is the funnel's own truth.
 */
export const signupStepBodySchema = z
    .object({
    step: z.enum(['details', 'code', 'address', 'rates', 'brand', 'done']),
    email: emailSchema.optional(),
    source: signupSourceSchema.optional(),
})
    .strict();
//# sourceMappingURL=signup.js.map